import {
  GET_RECORDING_DETAILS_FAILURE,
  GET_RECORDING_DETAILS_REQUEST,
  GET_RECORDING_DETAILS_SUCCESS
} from './RecordingDetailsActions';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { getRecordingDetails, getRecordingViewers } from '../../Apis/ReportingReadApi';
import { forceLoginIfExpired } from '../Utils/RedirectionUtils';
import * as G2SAnalytics from '../../Apis/InsightsReportApi';
import { RecordingSource } from '../../Utils/Constants';
import moment from 'moment-timezone';
import { viewDateSorter } from '../../Utils/MomentUtils';
import { G2WView } from '../../Model/Views/G2WView';
import { G2SView } from '../../Model/Views/G2SView';
import { G2SEmbeddedView } from '../../Model/Views/G2SEmbeddedView';

export function* getRecordingDetailsWatcher() {
  yield takeLatest(GET_RECORDING_DETAILS_REQUEST, getRecordingDetailsWorker);
}

export function getUserMetaKeys(state) {
  let { userKey, accountKey } = state.reduced_user.user;
  return { userKey, accountKey };
}

export function* getRecordingDetailsWorker({ payload }) {
  let { recordingKey, recordingSource, filter } = payload;

  try {
    const { userKey, accountKey } = yield select(getUserMetaKeys);
    // get g2w recording with viewer details
    let { data: g2wRecording } = yield call(getRecordingDetails, recordingKey, true);
    // get g2s recording details
    let { data: g2sRecordings = [] } = yield call(G2SAnalytics.getG2SRecordings, [recordingKey], userKey, accountKey);

    // all viewers
    let allViewers = g2wRecording.views || [];

    // get g2s recording viewers
    let g2sViewers = [];
    if (g2sRecordings.length > 0) {
      const { data } = yield call(G2SAnalytics.getG2SRecordingViews, g2sRecordings[0].contentKey);
      g2sViewers = data.views || [];
    }

    // get g2s embedded viewers
    let g2sEmbeddedViewers = [];
    const { data } = yield call(getRecordingViewers, recordingKey, RecordingSource.G2S_EMBEDDED);
    if (data && data._embedded && data._embedded.recordingViews) {
      g2sEmbeddedViewers = data._embedded.recordingViews;
    }

    const recordingDetails = buildRecordingDetails(recordingSource, g2wRecording, g2sRecordings[0]);
    const g2wViews = buildG2WViews(allViewers, g2sViewers);
    let g2sViews = buildG2SViews(recordingDetails, allViewers, g2sViewers);
    const g2sEmbeddedViews = buildG2SEmbeddedViews(g2sEmbeddedViewers);

    g2sViews = removeG2SEmbeddedViewsHack(g2sViews, g2sEmbeddedViews);

    const newFilter = { ...filter, fromTime: recordingDetails.datePublished };

    yield put({
      type: GET_RECORDING_DETAILS_SUCCESS,
      payload: {
        recordingDetails,
        g2wViews,
        g2sViews,
        g2sEmbeddedViews,
        filter: newFilter
      }
    });
  } catch (error) {
    yield put({ type: GET_RECORDING_DETAILS_FAILURE, error });
    console.error(error);
    forceLoginIfExpired(error);
  }
}

function buildRecordingDetails(recordingSource, g2wRecording = {}, g2sRecording = {}) {
  let datePublished;
  switch (recordingSource) {
    case RecordingSource.G2W:
      datePublished = g2wRecording.dateUploaded;
      break;
    case RecordingSource.G2S:
    case RecordingSource.G2S_EMBEDDED:
      datePublished = g2sRecording.publishedDate
        ? moment(g2sRecording.publishedDate).toISOString()
        : g2wRecording.dateUploaded;
      break;
    default:
    // do nothing
  }
  return {
    recordingSource,
    recordingKey: g2wRecording.recordingKey,
    contentKey: g2sRecording.contentKey,
    name: recordingSource === RecordingSource.G2W ? g2wRecording.title : g2sRecording.title,
    size: g2wRecording.size,
    datePublished: datePublished,
    videoLengthInMillis: g2sRecording.videoLength * 1000
  };
}

function buildG2WViews(allViewers = [], g2sViewers = []) {
  const g2wEmailsMap = new Map();
  allViewers.forEach(viewer => {
    const view = new G2WView();
    view.givenName = viewer.givenName;
    view.surName = viewer.surName;
    view.email = viewer.email;
    view.viewDate = viewer.viewDate;
    if (!g2wEmailsMap.has(viewer.email)) {
      g2wEmailsMap.set(viewer.email, [view]);
    } else {
      let existingViews = g2wEmailsMap.get(viewer.email);
      g2wEmailsMap.set(viewer.email, [...existingViews, view]);
    }
  });

  const g2wEmailsSet = new Set(g2sViewers.map(viewer => viewer.email));

  return [...g2wEmailsMap.keys()]
    .filter(email => !g2wEmailsSet.has(email))
    .reduce((result, email) => result.concat(g2wEmailsMap.get(email)), [])
    .sort(viewDateSorter);
}
function buildG2SViews(recordingDetails, allViewers = [], g2sViewers = []) {
  const allViewerMap = new Map();
  allViewers.forEach(view => {
    if (!allViewerMap.has(view.email)) {
      allViewerMap.set(view.email, [view]);
    } else {
      let existingViews = allViewerMap.get(view.email);
      allViewerMap.set(view.email, [...existingViews, view]);
    }
  });

  const g2sEmailsMap = new Map();
  g2sViewers.forEach(g2sView => {
    const { watchTime: watchTimeInPercent, viewDates = [], email } = g2sView;
    const webinarViews = allViewerMap.get(email) || [];
    const { givenName = '', surName = '' } = webinarViews.length > 0 ? webinarViews[0] : {};

    viewDates.forEach(viewDateEpoch => {
      const view = new G2SView();
      view.givenName = givenName;
      view.surName = surName;
      view.email = email;
      view.viewDate = moment(viewDateEpoch).toISOString();
      view.watchTimeInPercent = watchTimeInPercent;
      view.viewDurationInMillis = (recordingDetails.videoLengthInMillis * watchTimeInPercent) / 100;
      if (!g2sEmailsMap.has(email)) {
        g2sEmailsMap.set(email, [view]);
      } else {
        let existingViews = g2sEmailsMap.get(email);
        g2sEmailsMap.set(email, [...existingViews, view]);
      }
    });
  });

  return Array.from(g2sEmailsMap.values())
    .flatMap(viewList => [...viewList])
    .sort(viewDateSorter);
}
function buildG2SEmbeddedViews(g2sEmbeddedViewers = []) {
  const g2sEmbeddedMap = new Map();
  g2sEmbeddedViewers.forEach(viewer => {
    const { givenName = '', surName = '', email, viewDate, sourceEmbeddedLink = '' } = viewer;
    const view = new G2SEmbeddedView();
    view.givenName = givenName;
    view.surName = surName;
    view.email = email;
    view.viewDate = viewDate;
    view.sourceEmbeddedLink = sourceEmbeddedLink;

    if (!g2sEmbeddedMap.has(email)) {
      g2sEmbeddedMap.set(email, [view]);
    } else {
      let existingViews = g2sEmbeddedMap.get(email);
      g2sEmbeddedMap.set(viewer.email, [...existingViews, view]);
    }
  });

  return Array.from(g2sEmbeddedMap.values())
    .flatMap(viewList => [...viewList])
    .sort(viewDateSorter);
}

/**
 * HACK: we are getting g2s views as follows explanation can be found here https://confluence.ops.expertcity.com/display/collaboration/G2W+Analytics+SPA+G2S+views+hack
 * @param g2sViews
 * @param g2sEmbeddedViews
 * @returns {Array}
 */
function removeG2SEmbeddedViewsHack(g2sViews = [], g2sEmbeddedViews = []) {
  let copiedEmbeddedViews = [...g2sEmbeddedViews];
  let newG2SViews = [];

  g2sViews.forEach(view => {
    let indexInCopiedEmbedded = copiedEmbeddedViews.findIndex(
      embeddedView => embeddedView && embeddedView.email === view.email
    );
    if (indexInCopiedEmbedded !== -1) {
      copiedEmbeddedViews[indexInCopiedEmbedded] = null;
    } else {
      newG2SViews.push(view);
    }
  });

  return newG2SViews;
}

export default function* rootSaga() {
  yield all([fork(getRecordingDetailsWatcher)]);
}



// WEBPACK FOOTER //
// ./src/Reducks/RecordingDetails/RecordingDetailsSagas.js